// LivestreamDetailControlButtons.jsx
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import LiveStreamSettingButton from '../container/LiveStreamSettingButton.js';
import ShareButton from '../container/ShareButton.js';
import LivestreamRatingButton from '../container/LivestreamRatingButton.js';
import Tooltip from '../component/Tooltip.jsx';

import { ShareType } from '../hook/useShortShareLink.js';

import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';
import {
  PRESET_PREVIEW,
  privateShowStatus as privateShowConstants,
} from '../resource/liveStreamConstants.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import { color } from '../style/variables.js';

import AudioOnIconSrc from '../../img/ic-audio-on.svg';
import AudioOffIconSrc from '../../img/ic-audio-off.svg';
import OpenSidebarResource from '../../img/ic_open_sidebar.svg';
import CloseSidebarResource from '../../img/ic_close_sidebar.svg';
import GiftIconSrc from '../../img/icon-gift.png';
import ToyCommandIconSrc from '../../img/icon-toy-command.png';
import PrivateCtaIconSrc from '../../img/icon-private-cta.png';
import LinkIconSrc from '../../img/icon-link.png';

export const LivestreamDetailControlButtons = ({
  streamId = null,
  sessionId = '',
  username = '',
  streamStatus = null,
  isMuted = true,
  isAsideExpanded = true,
  isStreamActivated = false,
  isTriggerPrivatePanelOpen = false,
  isGiftPanelOpen = false,
  isToyCommandModalOpen = false,
  hasStreamToken = false,
  needStreamTokenForExclusivePrivatePerforming = false,
  toyCommandHintOpenedTimestamp = 0,
  userId = '',
  privateShowStatus = null,
  isKaraokeDeviceOnline = false,
  setMuted = NULL_FUNCTION,
  setAsideExpended = NULL_FUNCTION,
  openModal = NULL_FUNCTION,
  toggleModal = NULL_FUNCTION,
  toggleTriggerPrivatePanel = NULL_FUNCTION,
  setToyCommandHintOpenedTimestamp = NULL_FUNCTION,
  shareButtonTemplates = EMPTY_ARRAY,
}) => {
  const { t } = useTranslation();

  // Tooltip auto-display logic
  const shouldShowToyCommandHint = !toyCommandHintOpenedTimestamp;

  const handleTooltipClosed = useCallback(() => {
    if (shouldShowToyCommandHint) {
      setToyCommandHintOpenedTimestamp({
        toyCommandHintOpenedTimestamp: Date.now(),
      });
    }
  }, [shouldShowToyCommandHint, setToyCommandHintOpenedTimestamp]);

  const handleMuteOnClick = useCallback(() => {
    setMuted({ isMuted: !isMuted, isFirstUnmuted: false });
  }, [setMuted, isMuted]);
  const toggleDesktopAside = useCallback(() => {
    setAsideExpended({ isAsideExpanded: !isAsideExpanded });
  }, [setAsideExpended, isAsideExpanded]);
  const openToyCommandModal = useCallback(() => {
    toggleModal({
      modalId: 'LiveStreamKaraokeModal',
      payload: {
        streamId,
        mountElementId: isStreamActivated ? 'toy-command-button-desktop' : '',
      },
      isOpen: true,
    });
  }, [toggleModal, streamId, isStreamActivated]);
  const handleToggleTriggerPrivatePanel = useCallback(() => {
    toggleTriggerPrivatePanel({
      isTriggerPrivatePanelOpen: !isTriggerPrivatePanelOpen,
    });
  }, [toggleTriggerPrivatePanel, isTriggerPrivatePanelOpen]);
  const openLiveStreamGiftModal = useCallback(() => {
    openModal({ id: 'LiveStreamGiftModal' });
  }, [openModal]);

  const renderToyCommandButton = useCallback(() => {
    const shouldStopKaraokeIfPerforming = [
      privateShowConstants.PERFORMING,
      privateShowConstants.PREPARING,
    ].includes(privateShowStatus);

    const shouldHideToyButton =
      shouldStopKaraokeIfPerforming && !isKaraokeDeviceOnline;

    return (
      <DesktopToyCommandContainer $shouldHide={shouldHideToyButton}>
        <DesktopToyCommandWrapper>
          <Tooltip
            key={shouldShowToyCommandHint ? 'show-tooltip' : 'hide-tooltip'}
            position="left"
            trigger="auto"
            onHintClosed={handleTooltipClosed}
            renderHint={() => {
              if (shouldShowToyCommandHint) {
                return (
                  <DesktopToyCommandTooltipWrapper>
                    {t('tips_livestream_update_toy_commands', {
                      ns: TranslationNamespace.LIVESTREAM,
                    })}
                  </DesktopToyCommandTooltipWrapper>
                );
              }

              return null;
            }}
          >
            <DesktopToyCommandButton
              onClick={openToyCommandModal}
              $isActive={isToyCommandModalOpen}
              data-element_id={ButtonId.LiveStream.ButtonCommandList}
              data-sheet_mount_id={
                isStreamActivated ? 'toy-command-button-desktop' : ''
              }
            >
              <DesktopToyCommandIconContainer>
                <img src={ToyCommandIconSrc} alt="Toy Command" />
              </DesktopToyCommandIconContainer>
            </DesktopToyCommandButton>
          </Tooltip>
        </DesktopToyCommandWrapper>
      </DesktopToyCommandContainer>
    );
  }, [
    shouldShowToyCommandHint,
    handleTooltipClosed,
    t,
    openToyCommandModal,
    isToyCommandModalOpen,
    privateShowStatus,
    isKaraokeDeviceOnline,
    isStreamActivated,
  ]);

  return (
    <StyledLivestreamDetailControlButtons>
      {/* Gift */}
      {!needStreamTokenForExclusivePrivatePerforming && (
        <DesktopGiftButton
          $isActive={isGiftPanelOpen && isAsideExpanded}
          data-element_id={ButtonId.All.ButtonGift}
          onClick={openLiveStreamGiftModal}
        >
          <img src={GiftIconSrc} alt="Gift" />
        </DesktopGiftButton>
      )}
      {/* Private CTA */}
      {(streamStatus === PRESET_PREVIEW || hasStreamToken) && (
        <DesktopPrivateCtaButton
          $isActive={isTriggerPrivatePanelOpen && isAsideExpanded}
          data-element_id={ButtonId.LiveStream.ButtonUserTrigger}
          onClick={handleToggleTriggerPrivatePanel}
        >
          <img src={PrivateCtaIconSrc} alt="Private CTA" />
        </DesktopPrivateCtaButton>
      )}

      {/* Toy Command */}
      {renderToyCommandButton()}

      <LivestreamRatingButton id={streamId} />
      <LiveStreamSettingButton
        streamId={streamId}
        isActive={isStreamActivated}
      />
      <ShareButton
        id={sessionId}
        shareType={ShareType.LIVESTREAM}
        tooltipPosition={'left'}
        username={username}
        userId={userId}
        copyLinkBoxPosition={'center'}
        customShareIcon={LinkIconSrc}
        isAutoHandleShareText={true}
        templates={shareButtonTemplates}
      />
      <ControlButton
        data-element_id={
          isMuted ? ButtonId.All.ButtonMuted : ButtonId.All.ButtonVolume
        }
        onClick={handleMuteOnClick}
      >
        <img src={isMuted ? AudioOffIconSrc : AudioOnIconSrc} alt="Mute" />
      </ControlButton>
      <DesktopControlButton
        data-element_id={
          isAsideExpanded
            ? ButtonId.All.ButtonCloseSidebar
            : ButtonId.All.ButtonOpenSidebar
        }
        onClick={toggleDesktopAside}
        title={isAsideExpanded ? 'Close sidebar' : 'Open sidebar'}
      >
        <img
          src={isAsideExpanded ? CloseSidebarResource : OpenSidebarResource}
          alt="toggle sidebar"
        />
      </DesktopControlButton>
    </StyledLivestreamDetailControlButtons>
  );
};

LivestreamDetailControlButtons.propTypes = {
  streamId: PropTypes.string,
  sessionId: PropTypes.string,
  username: PropTypes.string,
  streamStatus: PropTypes.string,
  userId: PropTypes.string,
  isMuted: PropTypes.bool,
  isAsideExpanded: PropTypes.bool,
  isStreamActivated: PropTypes.bool,
  isTriggerPrivatePanelOpen: PropTypes.bool,
  isGiftPanelOpen: PropTypes.bool,
  hasStreamToken: PropTypes.bool,
  needStreamTokenForExclusivePrivatePerforming: PropTypes.bool,
  toyCommandHintOpenedTimestamp: PropTypes.number,
  isToyCommandModalOpen: PropTypes.bool,
  setMuted: PropTypes.func,
  setAsideExpended: PropTypes.func,
  openModal: PropTypes.func,
  toggleModal: PropTypes.func,
  toggleTriggerPrivatePanel: PropTypes.func,
  setToyCommandHintOpenedTimestamp: PropTypes.func,
  shareButtonTemplates: PropTypes.array,
  privateShowStatus: PropTypes.string,
  isKaraokeDeviceOnline: PropTypes.bool,
};

const StyledLivestreamDetailControlButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ControlButton = styled.button`
  ${ResetButtonStyle};
  min-width: 40px;
  min-height: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(25, 25, 25, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${color.neutral[100]};
  > img {
    object-fit: contain;
  }
`;

const DesktopControlButton = styled(ControlButton)`
  ${media.tablet`
    display: none;
  `};
`;

const DesktopGiftButton = styled(ControlButton)`
  position: relative;
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 24px;
    height: 24px;
  }
  ${media.tablet`
    display: none;
  `};
`;

const DesktopPrivateCtaButton = styled(ControlButton)`
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 24px;
    height: 24px;
  }
  ${media.tablet`
    display: none;
  `};
`;

const DesktopToyCommandButton = styled(ControlButton)`
  position: relative;
  z-index: 2;
  ${({ $isActive }) =>
    $isActive
      ? css`
          background-color: ${color.neutral['20_alpha_40']};
        `
      : css``}
  > img {
    width: 24px;
    height: 24px;
  }
  ${media.tablet`
    display: none;
  `};
`;

const DesktopToyCommandContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${props =>
    props.$shouldHide &&
    css`
      display: none;
    `}

  ${media.tablet`
    display: none;
  `};
`;

const DesktopToyCommandWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
`;

const DesktopToyCommandTooltipWrapper = styled.div`
  position: relative;
  border-radius: 4px;
  padding: 6px 8px;
  margin-right: 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.neutral[100]};
  background-color: ${color.neutral[0]};
  text-align: center;
  display: inline-block;
  width: max-content;
  max-width: 250px;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  box-shadow: 0px 2px 8px 0px rgba(25, 25, 25, 0.2);

  &::after {
    position: absolute;
    top: 50%;
    right: -4px;
    width: 8px;
    height: 8px;
    background-color: ${color.neutral[0]};
    transform: translateY(-50%) rotate(45deg);
    content: '';
  }
`;

const DesktopToyCommandIconContainer = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  will-change: transform;
  transform: translateZ(0);
  z-index: 2;

  > img {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  > img:first-child {
    /* ToyCommand icon */
    opacity: 1;
    transform: translate(-50%, -50%);
    will-change: transform, opacity;
  }
`;

export default LivestreamDetailControlButtons;
